/*!
* # SeriouslyRAD
* http://www.seriouslyrad.com/
* 
* Copyright © 2016 - 2026 Seriously RAD Limited - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*
*/

import { Constructor, Nullable, PartialNullable } from "./Extensions";
import { ArrayHelper } from "./Helpers/ArrayHelper";
import { ObjectHelper } from "./Helpers/ObjectHelper";
import { $$, IJNativeEvent, JNative } from "./JNative";
import { LinqArray } from "./LinqArray";
import { NAMESPACE } from "./_Namespace";
import { PropertyChange } from "./PropertyChange";
import { IAttribute } from "./Server/DotNet/System.Runtime/Attribute";
import { BuiltInTypes } from "./Server/DotNet/System/BuiltInTypes";

// #region Rad Object Info

export interface IRadObjectInfo<T>
{
    fullName: string;
    interfaceType?: T;
}

// #endregion

// #region Rad Object

const
    propertyDefaultOverridesMap = new Map<Constructor<RadObject<RadObjectPropertyValues>>, () => PartialNullable<RadObjectPropertyValues>>();

export type RadObjectConstructor<T extends IRadObject<U> = RadObject<RadObjectPropertyValues>, U extends RadObjectPropertyValues = RadObjectPropertyValues> = {
    attributes: IAttribute[];
    classFullName: string;
    className: string;
    classNamespace: string;
    name: string;
    new(properties?: any): T;
    createPropertyDefaults(): PartialNullable<U>;
}

export type RadObjectPropertyValues = {
};

export type RadObjectProperties = {
    [name in keyof RadObjectPropertyValues]: Property<RadObjectPropertyValues[name]>
};

export interface IRadObject<T extends RadObjectPropertyValues = RadObjectPropertyValues> extends RadObjectProperties
{
    dispose(): void;
    getEventHandlerTarget(eventName?: string): any;
    off(): void;
    off(events: string): void;
    off(events: string, handler: Function): void;
    off(events: string, selector: string, handler: Function): void;
    off(events: string, data: any, handler: Function): void;
    off(events: string, selector: string, data: any, handler: Function): void;
    off(events?: string, ...args: any[]): void;
    on(): void;
    on(events: string): void;
    on(events: string, handler: Function): void;
    on(events: string, selector: string, handler: Function): void;
    on(events: string, data: any, handler: Function): void;
    on(events: string, selector: string, data: any, handler: Function): void;
    on(events?: string, ...args: any[]): void;
    setProperties(properties: { [name: string]: any }): this;
    trigger(eventName: string, eventData?: any): void;
    triggerHandler(eventName: string, eventData?: any): void;
}

export class RadObject<TPropertyValues extends RadObjectPropertyValues = RadObjectPropertyValues> implements IRadObject<TPropertyValues>
{
    public static classNamespace = NAMESPACE + "RadObject";
    public static className = "RadObject";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Static

    public static attributes: IAttribute[] = [];

    // #region Public Methods

    public static createPropertyDefaults(): PartialNullable<RadObjectPropertyValues>
    {
        return {};
    }
    public static getProperties(radObject: IRadObject): { propertyName: string, property: Property }[]
    {
        return (
            Object.keys(radObject)
                .filter(item => (radObject as any)[item] instanceof Property)
                .map(
                    item => ({
                        propertyName: item,
                        property: (radObject as any)[item] as Property
                    })
                )
        );
    }
    public static getPropertiesNew(radObject: IRadObject): { propertyName: string, property: PropertyNew }[]
    {
        return (
            Object.keys(radObject)
                .filter(item => (radObject as any)[item] instanceof PropertyNew)
                .map(
                    item => ({
                        propertyName: item,
                        property: (radObject as any)[item] as PropertyNew
                    })
                )
        );
    }
    public static registerPropertyDefaultOverride<TPropertyValues extends RadObjectPropertyValues>(constructor: Constructor<RadObject<TPropertyValues>>, getPropertyOverridesFn: () => PartialNullable<TPropertyValues>)
    {
        propertyDefaultOverridesMap.set(constructor, getPropertyOverridesFn);
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public _disposed: boolean = false;

    protected _debounceTimeouts: { [name: string]: number } = {};
    protected _disposing: boolean = false;
    protected _eventHandlerTarget: any;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        const
            constructor = Object.getPrototypeOf(this).constructor as RadObjectConstructor;

        this.__initializeProperties({ ...constructor.createPropertyDefaults(), ...this.__getPropertyDefaultOverrides(), ...propertyValues });
        this._eventHandlerTarget = $$(this);
    }

    // #endregion

    // #region Public Methods

    public _getClassHierarchy(): string[]
    {
        return ObjectHelper.getClassHierarchy(this);
    }

    public dispose()
    {
        if (this._disposed)
            return;

        if (this._disposing)
            throw new Error("Object already disposing");

        this._disposing = true;

        //removePropertyBoundEvents(this);
        this.off();

        this._dispose();

        for (let property in this)
        {
            if (this[property] instanceof Property)
                (this[property] as Property).dispose();

            delete this[property];
        }

        this._disposed = true;
    }
    public getEventHandlerTarget(eventName?: string): any
    {
        return this._eventHandlerTarget;
    }
    //public getTypeInfo(): TypeInfo
    //{
    //    const
    //        constructor = this.constructor as RadObjectConstructor,
    //        typeInfo = new TypeInfo({
    //            attributes: constructor.attributes,
    //            properties:
    //                RadObject
    //                    .getPropertiesNew(this)
    //                    .map(item => ({
    //                        attributes: item.property.attributes,
    //                        name: item.propertyName,
    //                        propertyTypeId: item.property.dotNetType,
    //                    })),
    //            underlyingTypeId: constructor.classFullName,
    //        });

    //    return typeInfo;
    //}
    public off(): void
    public off(events: string): void
    public off(events: string, handler: Function): void
    public off(events: string, selector: string, handler: Function): void
    public off(events: string, data: any, handler: Function): void
    public off(events: string, selector: string, data: any, handler: Function): void
    public off(events?: string, ...args: any[]): void
    {
        if (!events)
            return;

        const
            eventNames = events.split(" ").filter(item => !!item);

        for (let eventName of eventNames)
        {
            const
                $$target = this.getEventHandlerTarget(eventName) as JNative | RadObject;

            if (!$$target)
                return;

            $$target.off(events, ...(args as [any, any, any]));
        }
    }
    public on(): void
    public on(events: string): void
    public on(events: string, handler: Function): void
    public on(events: string, selector: string, handler: Function): void
    public on(events: string, data: any, handler: Function): void
    public on(events: string, selector: string, data: any, handler: Function): void
    public on(events?: string, ...args: any[]): void
    {
        if (!events)
            return;

        const
            eventNames = events.split(" ").filter(item => !!item);

        for (let eventName of eventNames)
        {
            const
                $$target = this.getEventHandlerTarget(eventName) as JNative | RadObject;

            if (!$$target)
                return;

            $$target.on(events, ...(args as [any, any, any]));
        }
    }
    public setProperties(properties: { [name: string]: any }): this
    {
        LinqArray.create(Object.keys(properties))
            .each(propertyName =>
            {
                const
                    property = (this as any)[propertyName];

                if (property instanceof Property)
                {
                    property.set(properties[propertyName]);
                    return false;
                }
            });

        return this;
    }
    public trigger(eventName: string, eventData?: any): void
    {
        const
            $$target = this.getEventHandlerTarget(eventName) as JNative | RadObject;

        if (!$$target)
            return;

        $$target.trigger(eventName, eventData);
    }
    public triggerHandler(eventName: string, eventData?: any): void
    {
        const
            $$target = this.getEventHandlerTarget(eventName) as JNative | RadObject;

        if (!$$target)
            return;

        $$target.triggerHandler(eventName, eventData);
    }

    // #endregion

    // #region Protected Methods

    protected __getPropertyDefaultOverrides()
    {
        const
            defaultOverrides: PartialNullable<TPropertyValues>[] = [];

        let
            currentConstructor = Object.getPrototypeOf(this).constructor;

        while (currentConstructor)
        {
            const
                getPropertyOverridesFn = propertyDefaultOverridesMap.get(currentConstructor);

            if (getPropertyOverridesFn)
                defaultOverrides.push(getPropertyOverridesFn());

            currentConstructor = Object.getPrototypeOf(currentConstructor);
        }

        let
            propertyDefaultOverrides: PartialNullable<TPropertyValues> = {},
            propertyDefaultOverride;

        while (propertyDefaultOverride = defaultOverrides.pop())
            propertyDefaultOverrides = { ...propertyDefaultOverrides, ...propertyDefaultOverride };

        return propertyDefaultOverrides;
    }
    protected __initializeProperties(propertyValues: RadObjectPropertyValues)
    {
        /* Intentionally empty */
    }

    protected _debounceClear(key: string)
    {
        if (!this._debounceTimeouts[key])
            return;

        clearTimeout(this._debounceTimeouts[key]);
        delete this._debounceTimeouts[key];
    }
    protected _debounce(key: string, fn: Function, ms?: number)
    {
        this._debounceClear(key);

        this._debounceTimeouts[key] =
            window.setTimeout(() =>
            {
                this._debounceClear(key);
                fn && fn();
            }, ms);
    }
    protected _dispose()
    {
        /* Intentionally empty */
    }

    // #endregion

    // #endregion
}

// #endregion

// #region Property

//export interface IPropertyAttributes
//{
//    category: string;
//    displayName: string;
//    helpText?: string;
//    order?: string;
//    propertyEditorClass?: RadObjectConstructor;
//    propertyEditorProperties?: ControlPropertyValues;
//}

export interface IPropertyPropertyValues<TValue>
{
    context: RadObject;
    dotNetType: BuiltInTypes;
    initialValue?: Nullable<TValue>;
    changeHandler?: (change: PropertyChange<TValue>) => void;
    propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void };
    attributes?: IAttribute[];
}

export class Property<TValue extends any = any>
{
    public static classNamespace = NAMESPACE + "RadObject";
    public static className = "Property";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Global Variables

    public attributes?: IAttribute[];
    public dotNetType: BuiltInTypes;

    private _changeHandler?: (change: PropertyChange<TValue>) => void;
    private _changeHandlers: { handler: (change: PropertyChange<TValue>) => void, thisArg: any }[];
    private _context: RadObject;
    private _propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void };
    private _overridesGet: ((next: () => Nullable<TValue>) => Nullable<TValue>)[] = [];
    private _overridesSet: ((value: Nullable<TValue>, next: (value: Nullable<TValue>) => void) => void)[] = [];
    private _value: Nullable<TValue> = null;

    // #endregion

    // #region Constructor

    constructor(propertyValues: RadObject | IPropertyPropertyValues<TValue>)
    constructor(context: RadObject, dotNetType: BuiltInTypes, initialValue?: Nullable<TValue>, changeHandler?: (change: PropertyChange<TValue>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValues<TValue> | RadObject, dotNetType?: BuiltInTypes, initialValue?: Nullable<TValue>, changeHandler?: (change: PropertyChange<TValue>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        this._changeHandlers = [];

        if (propertyValuesOrContext instanceof RadObject)
        {
            this._context = propertyValuesOrContext;
            this.dotNetType = dotNetType ?? "System.Object, System.Private.CoreLib";
            this._value = initialValue === undefined ? null : initialValue;
            this._changeHandler = changeHandler;
            this._propertyBoundEvents = propertyBoundEvents;
            this.attributes = attributes;
        }
        else
        {
            this._context = propertyValuesOrContext.context;
            this.dotNetType = propertyValuesOrContext.dotNetType;
            this._value = propertyValuesOrContext.initialValue === undefined ? null : propertyValuesOrContext.initialValue;
            this._changeHandler = propertyValuesOrContext.changeHandler;
            this._propertyBoundEvents = propertyValuesOrContext.propertyBoundEvents;
            this.attributes = propertyValuesOrContext.attributes;
        }
    }

    // #endregion

    // #region Public Methods

    public addChangeHandler(handler: (change: PropertyChange<TValue>) => void, thisArg: any)
    {
        if (this._changeHandlers.some(item => item.handler === handler))
            return;

        this._changeHandlers.push({ handler, thisArg });
    }
    public dispose()
    {
        this._changeHandlers = [];

        for (const property in this)
            delete this[property];
    }
    public get(): Nullable<TValue>
    {
        let
            value = this._value;

        if (this._overridesGet.length)
        {
            let
                index = this._overridesGet.length - 1;

            const
                next = (): Nullable<TValue> =>
                {
                    index--;

                    if (index >= 0)
                        return this._overridesGet[index](next);

                    return value;
                };

            value = this._overridesGet[index](next);

            return value;
        }

        return this._value;
    }
    public getAttribute<T extends IAttribute>(attributeType: { new(...args: any): T }): Nullable<T>
    {
        return (
            ArrayHelper
                .firstOrDefault(
                    ArrayHelper.ofType(this.attributes ?? [], attributeType)
                )
        );
    }
    public removeChangeHandler(handler: (change: PropertyChange<TValue>) => void)
    {
        if (!this._changeHandlers.some(item => item.handler === handler))
            return;

        this._changeHandlers = this._changeHandlers.filter(item => item.handler !== handler);
    }
    public set(value: Nullable<TValue>, triggerEvent: boolean = true): boolean
    {
        if (this._overridesSet.length)
        {
            let
                index = this._overridesSet.length - 1,
                hasChanged = false;

            const
                next = (value: Nullable<TValue>): void =>
                {
                    index--;

                    if (index >= 0)
                        this._overridesSet[index](value, next);
                    else
                        hasChanged = this.setProperty(value, triggerEvent);
                };

            this._overridesSet[index](value, next);
            return hasChanged;
        }

        return this.setProperty(value, triggerEvent);
    }
    public setAttributes(attributes: IAttribute[])
    {
        // TODO: BAM
        this.attributes = attributes;
    }
    public overrideGet(f: (next: () => Nullable<TValue>) => Nullable<TValue>)
    {
        this._overridesGet.push(f);
    }
    public overrideSet(f: (value: Nullable<TValue>, next: (value: Nullable<TValue>) => void) => void)
    {
        this._overridesSet.push(f);
    }
    public triggerChange()
    {
        this._changeHandler?.call(this._context, new PropertyChange<TValue>(this._value, this._value));
    }

    // #endregion

    // #region Private Methods

    private bindPropertyBoundEvents(originalValue: Nullable<TValue>)
    {
        if (!this._propertyBoundEvents)
            return;

        for (let eventName of Object.keys(this._propertyBoundEvents))
        {
            if (originalValue && originalValue instanceof RadObject)
                (originalValue as RadObject).off(eventName, { self: this._context }, this._propertyBoundEvents[eventName]);

            if (this._value && this._value instanceof RadObject)
                (this._value as RadObject).on(eventName, { self: this._context }, this._propertyBoundEvents[eventName]);
        }
    }
    private setProperty(value: Nullable<TValue>, triggerEvent: boolean = true): boolean
    {
        // TODO: BAM
        if (value === undefined)
            return false;

        if (this._value === value)
            return false;

        const
            originalValue = this._value;

        this._value = ObjectHelper.asDotNetType(value, this.dotNetType);

        this.bindPropertyBoundEvents(originalValue);

        if (triggerEvent)
        {
            for (const handlerInfo of this._changeHandlers)
                handlerInfo.handler.call(handlerInfo.thisArg, new PropertyChange<TValue>(this._value, originalValue));

            this._changeHandler?.call(this._context, new PropertyChange<TValue>(this._value, originalValue));
        }

        return true;
    }

    // #endregion
}

export type PropertyNewPropertyValues<TValue> = {
    attributes?: IAttribute[];
    changeHandler?: (change: PropertyChange<TValue>) => void;
    context: RadObject;
    initialValue?: Nullable<TValue>;
    propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void };
    dotNetType: BuiltInTypes;
};

export type PropertyNewProperties<TValue> = {
    [name in keyof PropertyNewPropertyValues<TValue>]?: PropertyNewPropertyValues<TValue>[name];
};

export interface IPropertyNew<TValue> extends PropertyNewProperties<TValue>
{
}

export type PropertyNewConstructor<T, P extends PropertyNewPropertyValues<V>, V> = {
    new(propertyValues: Partial<P>): T;
    name: string;
}

export class PropertyNew<TValue extends any = any> implements IPropertyNew<TValue>
{
    public static classNamespace = NAMESPACE + "RadObject";
    public static className = "PropertyNew";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Global Variables

    public attributes?: IAttribute[];
    public readonly changeHandler?: (change: PropertyChange<TValue>) => void;
    public readonly context?: RadObject;
    public readonly initialValue?: TValue;
    public readonly propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void };
    public readonly dotNetType?: BuiltInTypes;


    private _overridesGet: ((next: () => TValue) => TValue)[] = [];
    private _overridesSet: ((value: TValue, next: (value: TValue) => void) => void)[] = [];
    private _value: TValue;

    // #endregion

    // #region Constructor

    constructor(propertyValues: Partial<PropertyNewPropertyValues<TValue>>)
    {
        if (!propertyValues.initialValue)
            throw new Error("initialValue is required");

        this.attributes = propertyValues.attributes;
        this.changeHandler = propertyValues.changeHandler;
        this.context = propertyValues.context;
        this.dotNetType = propertyValues.dotNetType;
        this._value = propertyValues.initialValue;
        this.propertyBoundEvents = propertyValues.propertyBoundEvents;
    }

    // #endregion

    // #region Public Methods

    public get(): TValue
    {
        let
            value = this._value;

        if (this._overridesGet.length)
        {
            let
                index = this._overridesGet.length - 1;

            const
                next = (): TValue =>
                {
                    index--;

                    if (index >= 0)
                        return this._overridesGet[index](next);

                    return value;
                };

            value = this._overridesGet[index](next);

            return value;
        }

        return this._value;
    }
    public getAttribute<T extends IAttribute>(attributeType: { new(...args: any): T }): Nullable<T>
    {
        return (
            ArrayHelper
                .firstOrDefault(
                    ArrayHelper.ofType(this.attributes ?? [], attributeType)
                )
        );
    }
    public set(value: TValue, triggerEvent: boolean = true): boolean
    {
        if (this._overridesSet.length)
        {
            let
                index = this._overridesSet.length - 1,
                hasChanged = false;

            const
                next = (value: TValue): void =>
                {
                    index--;

                    if (index >= 0)
                        this._overridesSet[index](value, next);
                    else
                        hasChanged = this.setProperty(value, triggerEvent);
                };

            this._overridesSet[index](value, next);
            return hasChanged;
        }

        return this.setProperty(value, triggerEvent);
    }
    public setAttributes(attributes: IAttribute[])
    {
        // TODO: BAM
        this.attributes = attributes;
    }
    public overrideGet(f: (next: () => TValue) => TValue)
    {
        this._overridesGet.push(f);
    }
    public overrideSet(f: (value: TValue, next: (value: TValue) => void) => void)
    {
        this._overridesSet.push(f);
    }
    public triggerChange()
    {
        this.changeHandler?.call(this.context, new PropertyChange<TValue>(this._value, this._value));
    }

    // #endregion

    // #region Private Methods

    private bindPropertyBoundEvents(originalValue: TValue)
    {
        if (!this.propertyBoundEvents)
            return;

        for (let eventName of Object.keys(this.propertyBoundEvents))
        {
            if (originalValue && originalValue instanceof RadObject)
                (originalValue as RadObject).off(eventName, { self: this.context }, this.propertyBoundEvents[eventName]);

            if (this._value && this._value instanceof RadObject)
                (this._value as RadObject).on(eventName, { self: this.context }, this.propertyBoundEvents[eventName]);
        }
    }
    private setProperty(value: TValue, triggerEvent: boolean = true): boolean
    {
        // TODO: BAM
        if (value === undefined)
            return false;

        if (this._value === value)
            return false;

        const
            originalValue = this._value;

        if (!this.dotNetType)
            throw new Error(".NET type not set");

        this._value = ObjectHelper.asDotNetType(value, this.dotNetType);

        this.bindPropertyBoundEvents(originalValue);

        if (triggerEvent)
            this.changeHandler?.call(this.context, new PropertyChange<TValue>(this._value, originalValue));

        return true;
    }

    // #endregion
}

export interface IPropertyPropertyValuesWithoutDotNetType<TValue>
{
    context: RadObject;
    initialValue?: Nullable<TValue>;
    changeHandler?: (change: PropertyChange<TValue>) => void;
    propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void };
    attributes?: IAttribute[];
}

export class PropertyBoolean extends Property<boolean>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyBoolean";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<boolean>)
    constructor(context: RadObject, initialValue?: Nullable<boolean>, changeHandler?: (change: PropertyChange<boolean>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<boolean> | RadObject, initialValue?: Nullable<boolean>, changeHandler?: (change: PropertyChange<boolean>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Boolean, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Boolean, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyChar extends Property<string>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyChar";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<string>)
    constructor(context: RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<string> | RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Char, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Char, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyDateTime extends Property<string>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyDateTime";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<string>)
    constructor(context: RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<string> | RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.DateTime, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.DateTime, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyDecimal extends Property<number>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyDecimal";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<number>)
    constructor(context: RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<number> | RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Decimal, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Decimal, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyDouble extends Property<number>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyDouble";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<number>)
    constructor(context: RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<number> | RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Double, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Double, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyGuid extends Property<string>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyGuid";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<string>)
    constructor(context: RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<string> | RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Guid, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Guid, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyInt16 extends Property<number>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyInt16";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<number>)
    constructor(context: RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<number> | RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Int16, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Int16, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyInt32 extends Property<number>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyInt32";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<number>)
    constructor(context: RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<number> | RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Int32, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Int32, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyInt64 extends Property<bigint>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyInt64";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<bigint>)
    constructor(context: RadObject, initialValue?: Nullable<bigint>, changeHandler?: (change: PropertyChange<bigint>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<bigint> | RadObject, initialValue?: Nullable<bigint>, changeHandler?: (change: PropertyChange<bigint>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Int64, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Int64, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyObject extends Property<any>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyObject";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<any>)
    constructor(context: RadObject, initialValue?: Nullable<any>, changeHandler?: (change: PropertyChange<any>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<any> | RadObject, initialValue?: Nullable<any>, changeHandler?: (change: PropertyChange<any>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Object, System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Object, System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertySingle extends Property<number>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertySingle";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<number>)
    constructor(context: RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<number> | RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.Single, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.Single, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyString extends Property<string>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyString";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<string>)
    constructor(context: RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<string> | RadObject, initialValue?: Nullable<string>, changeHandler?: (change: PropertyChange<string>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.String, System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.String, System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyUInt16 extends Property<number>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyUInt16";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<number>)
    constructor(context: RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<number> | RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.UInt16, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.UInt16, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyUInt32 extends Property<number>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyUInt32";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<number>)
    constructor(context: RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<number> | RadObject, initialValue?: Nullable<number>, changeHandler?: (change: PropertyChange<number>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.UInt32, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.UInt32, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

export class PropertyUInt64 extends Property<bigint>
{
    public static override classNamespace = NAMESPACE + "RadObject";
    public static override className = "PropertyUInt64";

    constructor(propertyValues: IPropertyPropertyValuesWithoutDotNetType<bigint>)
    constructor(context: RadObject, initialValue?: Nullable<bigint>, changeHandler?: (change: PropertyChange<bigint>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    constructor(propertyValuesOrContext: IPropertyPropertyValuesWithoutDotNetType<bigint> | RadObject, initialValue?: Nullable<bigint>, changeHandler?: (change: PropertyChange<bigint>) => void, propertyBoundEvents?: { [name: string]: (evt: IJNativeEvent, eventData: any) => void }, attributes?: IAttribute[])
    {
        if (propertyValuesOrContext instanceof RadObject)
            super(propertyValuesOrContext, "System.Nullable`1[[System.UInt64, System.Private.CoreLib]], System.Private.CoreLib", initialValue, changeHandler, propertyBoundEvents, attributes);
        else
            super({
                context: propertyValuesOrContext.context,
                dotNetType: "System.Nullable`1[[System.UInt64, System.Private.CoreLib]], System.Private.CoreLib",
                initialValue: propertyValuesOrContext.initialValue,
                changeHandler: propertyValuesOrContext.changeHandler,
                propertyBoundEvents: propertyValuesOrContext.propertyBoundEvents,
                attributes: propertyValuesOrContext.attributes,
            });
    }
}

// #endregion
